import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Form, Spinner } from "react-bootstrap";
import { FaChevronLeft, FaFileUpload, FaSyncAlt } from "react-icons/fa";
import { authHeaders } from "./QuoteCreate";
import { getUser, hasAction } from "./utils/auth";
import "./pagecss/Webappcss.css";
import "./pagecss/QuoteWizard.css";

const API_URL = process.env.REACT_APP_API_URL;

function fmtMoney(v) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}

// Status → pill class + label. PoCovered means "held for a PO, not yet
// ordered" — isOpen is still true for it, so it stays selectable for convert.
function statusPill(s) {
  const map = {
    Open:      { cls: "qw-pill-indigo",  label: "Open" },
    PoCovered: { cls: "qw-pill-amber",   label: "Held for PO" },
    Converted: { cls: "qw-pill-emerald", label: "Converted" },
    Cancelled: { cls: "qw-pill-slate",   label: "Cancelled" },
  };
  const m = map[s] || { cls: "qw-pill-slate", label: s || "—" };
  return <span className={`qw-pill ${m.cls}`}>{m.label}</span>;
}

const verdictPillClass = {
  Covered: "qw-pill-emerald",
  CoveredExcludingTax: "qw-pill-amber",
  Partial: "qw-pill-amber",
  PartialLine: "qw-pill-amber",
  ComponentPriced: "qw-pill-amber",
  Ambiguous: "qw-pill-amber",
  Short: "qw-pill-amber",
  Over: "qw-pill-amber",
  NotCovered: "qw-pill-slate",
  Unmatched: "qw-pill-slate",
};

// =============================================================================
// MspQuote — the multi-shipment board for one quote: what's open, PO upload +
// coverage, claim/release, and partial conversion. This page is the whole
// repeat-per-PO phase of a multi-shipment plan; the quote screen links here.
//
// Everything renders from the API's own words: coverage `message` and plan
// rejections are bound verbatim (they're written for a person), the selection
// pre-ticks `proposedParentQuoteLines` (never re-derived client-side), and the
// convert panel renders `convertedLines` as read back from Epicor — never the
// lines that were asked for.
// =============================================================================
export default function MspQuote() {
  const { quoteNum: quoteNumParam } = useParams();
  const navigate = useNavigate();
  const quoteNum = Number(quoteNumParam);

  // ── Board ────────────────────────────────────────────────────────────────
  const [board, setBoard] = useState(null);
  const [boardLoading, setBoardLoading] = useState(false);
  const [boardError, setBoardError] = useState(null);

  // ── Selection + PO number (drives claim / release / convert) ─────────────
  const [selected, setSelected] = useState(() => new Set());
  const [poNumber, setPoNumber] = useState("");

  // ── Upload + extraction ──────────────────────────────────────────────────
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [extraction, setExtraction] = useState(null); // { attachmentId, method, confidence, poNumber, poAmount, note }
  const fileInputRef = useRef(null);

  // Manual PO entry — the escape hatch when extraction read nothing (00041).
  const [manualPo, setManualPo] = useState({ poNumber: "", poAmount: "" });
  const [manualSaving, setManualSaving] = useState(false);

  // Re-check coverage for an attachment uploaded earlier (or elsewhere) —
  // coverage is read-only, so it is safe to run as often as needed.
  const [attIdField, setAttIdField] = useState("");

  // ── Coverage ─────────────────────────────────────────────────────────────
  const [coverage, setCoverage] = useState(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [coverageError, setCoverageError] = useState(null);

  // ── Claim / convert ──────────────────────────────────────────────────────
  const [acting, setActing] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);   // { variant, text }
  const [convertResult, setConvertResult] = useState(null);

  const actor = getUser().name || getUser().email || "web";

  // The board re-reads Epicor every time, so it is never cached — a shipment
  // converted directly in Kinetic shows up here as Converted.
  const loadBoard = useCallback(async () => {
    if (!quoteNum) return;
    setBoardLoading(true);
    setBoardError(null);
    try {
      const r = await fetch(`${API_URL}/api/Msp/quote/${quoteNum}/shipments`, { headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false) {
        throw new Error(data.error || data.message || (r.status === 404
          ? `Quote ${quoteNum} has no recorded shipments — is it a multi-shipment quote?`
          : `HTTP ${r.status}`));
      }
      setBoard(data);
      // Drop selections that are no longer open (e.g. just converted).
      setSelected((prev) => {
        const stillOpen = new Set(
          (data.shipments || [])
            .filter((s) => s.isOpen && !s.missingInEpicor)
            .map((s) => s.parentQuoteLine)
        );
        return new Set([...prev].filter((l) => stillOpen.has(l)));
      });
    } catch (err) {
      setBoard(null);
      setBoardError(err.message);
    } finally {
      setBoardLoading(false);
    }
  }, [quoteNum]);

  useEffect(() => { loadBoard(); }, [loadBoard]);

  const toggleLine = (parentQuoteLine) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(parentQuoteLine)) next.delete(parentQuoteLine);
      else next.add(parentQuoteLine);
      return next;
    });
  };

  // ── Coverage — what does this PO pay for? Read-only, safe to repeat. ─────
  const runCoverage = useCallback(async (attachmentId) => {
    if (!attachmentId) return;
    setCoverageLoading(true);
    setCoverageError(null);
    try {
      const r = await fetch(
        `${API_URL}/api/Msp/quote/${quoteNum}/po-coverage/${attachmentId}`,
        { headers: authHeaders() }
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false) {
        throw new Error(data.error || data.message || `HTTP ${r.status}`);
      }
      setCoverage(data);
      // The matcher's proposal is the selection — never re-derived here. The
      // operator can still change it in the board before converting.
      setSelected(new Set(data.proposedParentQuoteLines || []));
      if (data.poNumber) setPoNumber(String(data.poNumber));
    } catch (err) {
      setCoverage(null);
      setCoverageError(err.message);
    } finally {
      setCoverageLoading(false);
    }
  }, [quoteNum]);

  // ── Upload — SAS → PUT to blob → confirm (the extraction cascade). ───────
  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    setExtraction(null);
    setCoverage(null);
    setCoverageError(null);
    setActionMsg(null);
    setConvertResult(null);
    try {
      const sasRes = await fetch(`${API_URL}/api/Attachment/sas-token`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          sourceQuoteNum: quoteNum,
          fileName: file.name,
          attachmentType: "PODocument",
          contentType: file.type || "application/pdf",
        }),
      });
      const sas = await sasRes.json().catch(() => null);
      if (!sasRes.ok || !sas?.success || !sas.sasUploadUrl) {
        throw new Error(sas?.error || "Could not allocate an upload slot.");
      }

      // The bytes go straight to blob storage — the SAS is the credential, so
      // no Authorization header here (Azure would reject a bearer token).
      const putRes = await fetch(sas.sasUploadUrl, {
        method: "PUT",
        headers: { "x-ms-blob-type": "BlockBlob", "Content-Type": file.type || "application/pdf" },
        body: file,
      });
      if (!putRes.ok) throw new Error(`Blob upload failed (HTTP ${putRes.status}).`);

      // Confirm runs the extraction cascade synchronously — regex, then Azure
      // Document Intelligence, then an LLM — so this can take ~20 seconds.
      const confRes = await fetch(`${API_URL}/api/Attachment/${sas.attachmentId}/confirm`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({ fileSizeBytes: file.size }),
      });
      const conf = await confRes.json().catch(() => null);
      if (!confRes.ok || !conf?.success) {
        throw new Error(conf?.error || "Extraction failed.");
      }

      // confirmedPONumber stays null for multi-ship on purpose — the generic
      // Net-30 reconciler compares the PO to the WHOLE quote total and a
      // partial PO is by definition less. Coverage below is the authority.
      setExtraction({
        attachmentId: conf.attachmentId ?? sas.attachmentId,
        fileName: conf.fileName || file.name,
        method: conf.extractionMethod,
        confidence: conf.extractionConfidence,
        poNumber: conf.extractedPONumber,
        poAmount: conf.extractedPOAmount,
      });
      if (conf.extractedPONumber) {
        await runCoverage(conf.attachmentId ?? sas.attachmentId);
      }
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // Manual override — extraction read nothing (or read it wrong): the operator
  // types the PO number + amount, then coverage runs on the corrected values.
  const saveManualPo = async () => {
    const num = manualPo.poNumber.trim();
    const amt = Number(manualPo.poAmount);
    if (!extraction?.attachmentId || !num || !(amt > 0)) return;
    setManualSaving(true);
    setUploadError(null);
    try {
      const r = await fetch(`${API_URL}/api/Attachment/${extraction.attachmentId}/confirm-po`, {
        method: "PATCH", headers: authHeaders(),
        body: JSON.stringify({ poNumber: num, poAmount: amt, confirmedBy: actor }),
      });
      const data = await r.json().catch(() => null);
      if (!r.ok || data?.success === false) {
        throw new Error(data?.message || data?.error || "Could not save the correction.");
      }
      setExtraction((prev) => ({ ...prev, poNumber: num, poAmount: amt, method: "Manual" }));
      await runCoverage(extraction.attachmentId);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setManualSaving(false);
    }
  };

  // ── Claim / release / convert ────────────────────────────────────────────
  const lines = [...selected].sort((a, b) => a - b);

  const doClaim = async () => {
    setActing(true);
    setActionMsg(null);
    try {
      const r = await fetch(`${API_URL}/api/Msp/quote/${quoteNum}/claim-shipments`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          parentQuoteLines: lines,
          customerPONumber: poNumber.trim(),
          claimedBy: actor,
          poAttachmentId: extraction?.attachmentId ?? null,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false) {
        throw new Error(data.error || data.message || `HTTP ${r.status}`);
      }
      setActionMsg({ variant: "info", text: data.message || `Held for PO ${poNumber}.` });
      await loadBoard();
    } catch (err) {
      setActionMsg({ variant: "danger", text: err.message });
    } finally {
      setActing(false);
    }
  };

  const doRelease = async (po) => {
    setActing(true);
    setActionMsg(null);
    try {
      const r = await fetch(
        `${API_URL}/api/Msp/quote/${quoteNum}/release-po/${encodeURIComponent(po)}`,
        { method: "POST", headers: authHeaders() }
      );
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false) {
        throw new Error(data.error || data.message || `HTTP ${r.status}`);
      }
      setActionMsg({ variant: "info", text: data.message || `PO ${po} released.` });
      await loadBoard();
    } catch (err) {
      setActionMsg({ variant: "danger", text: err.message });
    } finally {
      setActing(false);
    }
  };

  const doConvert = async () => {
    setActing(true);
    setActionMsg(null);
    setConvertResult(null);
    try {
      const r = await fetch(`${API_URL}/api/Msp/quote/${quoteNum}/convert-shipments`, {
        method: "POST", headers: authHeaders(),
        body: JSON.stringify({
          quoteNum,
          parentQuoteLines: lines,
          customerPONumber: poNumber.trim(),
          convertedBy: actor,
        }),
      });
      const data = await r.json().catch(() => ({}));
      setConvertResult(data);
      if (!r.ok || data.success === false) {
        setActionMsg({ variant: "danger", text: data.error || data.message || `HTTP ${r.status}` });
      }
      await loadBoard();
    } catch (err) {
      setActionMsg({ variant: "danger", text: err.message });
    } finally {
      setActing(false);
    }
  };

  const canUpload = hasAction("00040", true);
  const canCorrect = hasAction("00041", true);
  const canConvert = hasAction("00039", true);

  // Convert is gated on the operator's inputs, not on the coverage verdict —
  // coverage proposes, a person disposes (and may override the selection).
  const actionReady = lines.length > 0 && poNumber.trim().length > 0 && !acting;

  const shipments = board?.shipments || [];
  const heldPos = [...new Set(
    shipments.filter((s) => s.status === "PoCovered" && s.customerPONumber)
      .map((s) => s.customerPONumber)
  )];

  // Per-shipment quote document — `shipment` is the 1-based ORDINAL (the
  // sequence), not the quote line: the one place the MSP API keys on ordinals.
  // Delivered as an <a download> click so the filename survives — including in
  // the mobile WebView, whose download bridge reads the anchor's download
  // attribute (window.open(blob:) would arrive on device as "Quote.pdf").
  const openShipmentPdf = async (sequence) => {
    try {
      const r = await fetch(
        `${API_URL}/api/reports/quote/${quoteNum}?format=pdf&download=true&shipment=${sequence}`,
        { headers: authHeaders() }
      );
      if (!r.ok) throw new Error(`PDF failed (HTTP ${r.status})`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Quote_${quoteNum}-${sequence}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      setActionMsg({ variant: "danger", text: err.message });
    }
  };

  return (
    <div className="qw-root msp-page content-halo dyn-form-shell dyn-form-2x" style={{ padding: "28px 24px 36px", borderRadius: 16 }}>
      <div className="dyn-form-scroll">

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
          <button className="qw-btn qw-btn-ghost qw-btn-sm" onClick={() => navigate(`/quote/edit/${quoteNum}`)}>
            <FaChevronLeft /> Quote #{quoteNum}
          </button>
          <h2 className="qw-h2" style={{ margin: 0 }}>Shipments &amp; POs</h2>
          <span className="qw-pill qw-pill-violet">Multi-Shipment</span>
          <div style={{ flex: 1 }} />
          <button className="qw-btn qw-btn-outline qw-btn-sm" onClick={loadBoard} disabled={boardLoading}>
            <FaSyncAlt style={{ marginRight: 6 }} /> Refresh
          </button>
        </div>

        {boardError && <Alert variant="danger">{boardError}</Alert>}

        {/* ── Progress ───────────────────────────────────────────────────── */}
        {board && (
          <div className="qw-card-flat qw-pad" style={{ marginBottom: 14 }}>
            <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
              {[
                ["Shipments", board.shipmentCount],
                ["Open", board.openCount],
                ["Converted", board.convertedCount],
                ["Still to sell", fmtMoney(board.openValue)],
              ].map(([k, v]) => (
                <div key={k}>
                  <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5, color: "#94A3B8", fontWeight: 700 }}>{k}</div>
                  <div className="qw-mono" style={{ fontSize: 18, fontWeight: 700, color: "#0F172A" }}>{v}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── The board ──────────────────────────────────────────────────── */}
        <div className="qw-card-flat qw-pad" style={{ marginBottom: 14 }}>
          <div className="qw-eyebrow" style={{ marginBottom: 10 }}>
            Shipment board
            {boardLoading && <Spinner animation="border" size="sm" style={{ marginLeft: 8, width: 12, height: 12 }} />}
          </div>
          {/* min-width keeps the 11 columns legible on a phone — the container
              scrolls sideways instead of crushing the money columns (same
              fix class as mobile QA tickets T-003/T-004). */}
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", minWidth: 760, borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  {["", "#", "Token", "Line", "Net", "Tax", "PO must say", "Status", "PO", "Order", "Doc"].map((h, i) => (
                    <th key={i} style={{
                      textAlign: ["Net", "Tax", "PO must say"].includes(h) ? "right" : "left",
                      fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4,
                      color: "#94A3B8", padding: "6px 8px", borderBottom: "1px solid #E2E8F0",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {shipments.length === 0 && !boardLoading && (
                  <tr><td colSpan={11} style={{ padding: 12, color: "#94A3B8" }}>No shipments recorded on this quote.</td></tr>
                )}
                {shipments.map((s) => {
                  const selectable = s.isOpen && !s.missingInEpicor;
                  return (
                    <tr key={s.parentQuoteLine} style={{ background: s.missingInEpicor ? "#FEF2F2" : undefined }}>
                      <td style={{ padding: "6px 8px" }}>
                        <Form.Check
                          type="checkbox"
                          aria-label={`Select shipment ${s.sequence}`}
                          disabled={!selectable}
                          checked={selected.has(s.parentQuoteLine)}
                          onChange={() => toggleLine(s.parentQuoteLine)}
                        />
                      </td>
                      <td className="qw-mono" style={{ padding: "6px 8px", color: "#64748B" }}>{s.sequence}</td>
                      <td className="qw-mono" style={{ padding: "6px 8px", fontWeight: 600 }}>{s.token || "—"}</td>
                      <td className="qw-mono" style={{ padding: "6px 8px", color: "#64748B" }}>{s.parentQuoteLine}</td>
                      <td className="qw-mono" style={{ padding: "6px 8px", textAlign: "right" }}>
                        {fmtMoney(s.net)}
                        {s.driftsFromPlan && (
                          <div>
                            <span className="qw-pill qw-pill-amber" title={`Planned ${fmtMoney(s.planNet)} — someone repriced this line; the customer's quoted figure no longer matches.`}>
                              drifted
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="qw-mono" style={{ padding: "6px 8px", textAlign: "right" }}>{fmtMoney(s.tax)}</td>
                      <td className="qw-mono" style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700, color: "#4F46E5" }}>{fmtMoney(s.gross)}</td>
                      <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                        {statusPill(s.status)}
                        {s.missingInEpicor && <span className="qw-pill qw-pill-amber" style={{ marginLeft: 4, background: "#FEE2E2", color: "#991B1B" }} title="The ledger has this row but Epicor no longer does — the quote line was deleted.">gone</span>}
                      </td>
                      <td className="qw-mono" style={{ padding: "6px 8px" }}>{s.customerPONumber || "—"}</td>
                      <td className="qw-mono" style={{ padding: "6px 8px" }}>
                        {s.orderNum
                          ? <button className="qw-btn qw-btn-outline qw-btn-sm" onClick={() => navigate(`/order/${s.orderNum}`)}>#{s.orderNum}</button>
                          : "—"}
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        {hasAction("00033", true) && (
                          <button className="qw-btn qw-btn-ghost qw-btn-sm" title={`Shipment ${s.sequence} PDF`} onClick={() => openShipmentPdf(s.sequence)}>PDF</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: 8, fontSize: 11.5, color: "#94A3B8", lineHeight: 1.5 }}>
            <strong>PO must say</strong> is the whole-line figure (net + tax) the customer&apos;s purchase order is
            matched against — a PO that pays for part of a shipment buys none of it. Per-shipment documents
            deliberately leave out whole-quote charges (freight), so they don&apos;t sum to the quote total.
          </div>
        </div>

        {/* ── Upload a purchase order ────────────────────────────────────── */}
        {canUpload && (
          <div className="qw-card-flat qw-pad" style={{ marginBottom: 14 }}>
            <div className="qw-eyebrow" style={{ marginBottom: 10 }}>Upload a purchase order</div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              <Form.Control
                ref={fileInputRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg"
                disabled={uploading}
                onChange={(e) => handleFile(e.target.files?.[0])}
                style={{ maxWidth: 380 }}
              />
              {uploading && (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "#4F46E5", fontWeight: 600, fontSize: 13 }}>
                  <Spinner animation="border" size="sm" /> Uploading &amp; extracting — this can take ~20&nbsp;seconds…
                </span>
              )}
            </div>
            {uploadError && <Alert variant="danger" style={{ marginTop: 10, marginBottom: 0 }}>{uploadError}</Alert>}

            {/* A PO already uploaded (here or anywhere else) can be re-checked
                by its attachment id — coverage is read-only and repeatable. */}
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginTop: 12 }}>
              <div>
                <Form.Label className="small mb-1">Attachment id</Form.Label>
                <Form.Control size="sm" type="number" min={1} value={attIdField}
                  placeholder="from an earlier upload"
                  onChange={(e) => setAttIdField(e.target.value)} style={{ width: 170 }} />
              </div>
              <button className="qw-btn qw-btn-outline qw-btn-sm"
                disabled={coverageLoading || !(Number(attIdField) > 0)}
                onClick={() => runCoverage(Number(attIdField))}>
                Check coverage
              </button>
            </div>

            {extraction && (
              <div style={{ marginTop: 12 }}>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 13 }}>
                  <div><span style={{ color: "#94A3B8" }}>File</span> <span className="qw-mono">{extraction.fileName}</span></div>
                  <div><span style={{ color: "#94A3B8" }}>Read by</span> <span className="qw-mono">{extraction.method || "—"}{extraction.confidence != null ? ` · ${Math.round(extraction.confidence * 100)}%` : ""}</span></div>
                  <div><span style={{ color: "#94A3B8" }}>PO number</span> <span className="qw-mono" style={{ fontWeight: 700 }}>{extraction.poNumber || "—"}</span></div>
                  <div><span style={{ color: "#94A3B8" }}>PO amount</span> <span className="qw-mono" style={{ fontWeight: 700 }}>{fmtMoney(extraction.poAmount)}</span></div>
                </div>

                {!extraction.poNumber && (
                  <div className="qw-info qw-info-amber" style={{ marginTop: 10, fontSize: 12.5 }}>
                    Nothing was read from the document, so coverage can&apos;t run.
                    {canCorrect ? " Enter the PO number and amount by hand:" : " Someone with the PO-correction permission needs to enter it by hand."}
                  </div>
                )}
                {canCorrect && (
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginTop: 10 }}>
                    <div>
                      <Form.Label className="small mb-1">PO number</Form.Label>
                      <Form.Control size="sm" value={manualPo.poNumber}
                        placeholder={extraction.poNumber || ""}
                        onChange={(e) => setManualPo((m) => ({ ...m, poNumber: e.target.value }))} style={{ width: 160 }} />
                    </div>
                    <div>
                      <Form.Label className="small mb-1">PO amount ($)</Form.Label>
                      <Form.Control size="sm" type="number" min={0} step="0.01" value={manualPo.poAmount}
                        placeholder={extraction.poAmount != null ? String(extraction.poAmount) : ""}
                        onChange={(e) => setManualPo((m) => ({ ...m, poAmount: e.target.value }))} style={{ width: 140 }} />
                    </div>
                    <button className="qw-btn qw-btn-outline qw-btn-sm" disabled={manualSaving || !manualPo.poNumber.trim() || !(Number(manualPo.poAmount) > 0)} onClick={saveManualPo}>
                      {manualSaving ? <Spinner animation="border" size="sm" /> : "Correct & re-check"}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── Coverage — the proposal ────────────────────────────────────── */}
        {(coverage || coverageLoading || coverageError) && (
          <div className="qw-card-flat qw-pad" style={{ marginBottom: 14 }}>
            <div className="qw-eyebrow" style={{ marginBottom: 10 }}>
              What does this PO pay for?
              {coverageLoading && <Spinner animation="border" size="sm" style={{ marginLeft: 8, width: 12, height: 12 }} />}
            </div>
            {coverageError && <Alert variant="danger" style={{ marginBottom: 0 }}>{coverageError}</Alert>}
            {coverage && (
              <>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
                  <div><span style={{ color: "#94A3B8", fontSize: 12 }}>PO</span> <span className="qw-mono" style={{ fontWeight: 700 }}>{coverage.poNumber || "—"}</span></div>
                  <div><span style={{ color: "#94A3B8", fontSize: 12 }}>Total</span> <span className="qw-mono" style={{ fontWeight: 700 }}>{fmtMoney(coverage.poTotal)}</span></div>
                  <div><span style={{ color: "#94A3B8", fontSize: 12 }}>Basis</span> <span className="qw-mono">{coverage.matchBasis || "—"}</span></div>
                  <span className={`qw-pill ${verdictPillClass[coverage.verdict] || "qw-pill-slate"}`}>{coverage.verdict}</span>
                  {coverage.actionable === false && <span className="qw-pill qw-pill-amber" style={{ background: "#FEE2E2", color: "#991B1B" }}>not actionable</span>}
                  <div><span style={{ color: "#94A3B8", fontSize: 12 }}>Covers</span> <span className="qw-mono" style={{ fontWeight: 700 }}>{fmtMoney(coverage.coveredTotal)}</span></div>
                </div>

                {/* The message is written for a person — bound verbatim, never paraphrased. */}
                <Alert variant={coverage.actionable ? "info" : "warning"} style={{ fontSize: 13 }}>{coverage.message}</Alert>

                {coverage.identifiedByToken ? (
                  <div className="qw-info qw-info-emerald" style={{ fontSize: 12.5, marginBottom: 10 }}>
                    <strong>Identified by token.</strong> The purchase order named the shipments
                    {(coverage.evidence || []).length > 0 && <> ({(coverage.evidence || []).join(", ")})</>},
                    so the match is unambiguous.
                  </div>
                ) : (
                  <div className="qw-info qw-info-amber" style={{ fontSize: 12.5, marginBottom: 10 }}>
                    <strong>Matched on amount only</strong> — no shipment token was read from the document
                    {coverage.identityAmbiguous
                      ? ", and more than one set of shipments fits this figure. Confirm (or change) the selection on the board before converting."
                      : ". Confirm the selection on the board before converting."}
                  </div>
                )}

                {Number(coverage.taxShortfall) > 0 && (
                  <div className="qw-info qw-info-amber" style={{ fontSize: 12.5, marginBottom: 10 }}>
                    <strong>Tax shortfall {fmtMoney(coverage.taxShortfall)}.</strong> The PO matched the net figure,
                    so the customer appears to have ordered without tax. Either they are exempt — have CS clear the
                    tax in Epicor and re-check — or the PO is short.
                  </div>
                )}

                {(coverage.covered || []).length > 0 && (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", minWidth: 460, borderCollapse: "collapse", fontSize: 12.5 }}>
                      <thead>
                        <tr>
                          {["#", "Token", "Line", "Amount", "Confidence"].map((h, i) => (
                            <th key={h} style={{ textAlign: i >= 3 ? "right" : "left", fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.4, color: "#94A3B8", padding: "5px 8px", borderBottom: "1px solid #E2E8F0" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {coverage.covered.map((c) => (
                          <tr key={c.parentQuoteLine}>
                            <td className="qw-mono" style={{ padding: "5px 8px", color: "#64748B" }}>{c.sequence}</td>
                            <td className="qw-mono" style={{ padding: "5px 8px" }}>{c.token}</td>
                            <td className="qw-mono" style={{ padding: "5px 8px", color: "#64748B" }}>{c.parentQuoteLine}</td>
                            <td className="qw-mono" style={{ padding: "5px 8px", textAlign: "right", fontWeight: 600 }}>{fmtMoney(c.amount)}</td>
                            <td className="qw-mono" style={{ padding: "5px 8px", textAlign: "right", color: "#64748B" }}>{c.confidence != null ? `${Math.round(c.confidence * 100)}%` : "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ marginTop: 6, fontSize: 11.5, color: "#94A3B8" }}>
                      These shipments are pre-selected on the board above — change the selection there if the
                      proposal is wrong.
                    </div>
                  </div>
                )}
                {(coverage.covered || []).length === 0 && (
                  <div className="qw-info qw-info-amber" style={{ fontSize: 12.5 }}>
                    This purchase order covers no whole shipment, so nothing can convert.
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Actions — claim / release / convert ────────────────────────── */}
        <div className="qw-card-flat qw-pad" style={{ marginBottom: 14 }}>
          <div className="qw-eyebrow" style={{ marginBottom: 10 }}>Convert selected shipments</div>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div>
              <Form.Label className="small mb-1">Customer PO #</Form.Label>
              <Form.Control size="sm" value={poNumber} onChange={(e) => setPoNumber(e.target.value)} style={{ width: 160 }} />
            </div>
            <div style={{ fontSize: 13, color: "#64748B", paddingBottom: 6 }}>
              {lines.length === 0 ? "No shipments selected." : (
                <>Selected lines: <span className="qw-mono" style={{ fontWeight: 700, color: "#0F172A" }}>{lines.join(", ")}</span></>
              )}
            </div>
            <div style={{ flex: 1 }} />
            {canCorrect && (
              <button className="qw-btn qw-btn-outline qw-btn-sm" disabled={!actionReady} onClick={doClaim}
                title="Mark these shipments as spoken for by this PO, so another PO isn't offered them">
                Claim (hold)
              </button>
            )}
            {canConvert && (
              <button className="qw-btn qw-btn-primary" disabled={!actionReady} onClick={doConvert}>
                {acting ? <Spinner animation="border" size="sm" /> : <><FaFileUpload style={{ marginRight: 6 }} /> Convert to sales order</>}
              </button>
            )}
          </div>
          <div className="qw-info qw-info-amber" style={{ marginTop: 10, fontSize: 12 }}>
            Converting is real and not reversible from here: it creates an Epicor sales order, stamps the ship date
            at order date + 10 days, and queues the acknowledgement email to the customer with the rep copied.
          </div>

          {canCorrect && heldPos.length > 0 && (
            <div style={{ marginTop: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 12.5, color: "#64748B" }}>
              Held POs:
              {heldPos.map((po) => (
                <button key={po} className="qw-btn qw-btn-ghost qw-btn-sm" disabled={acting} onClick={() => doRelease(po)} title={`Release the unconverted claim for PO ${po}`}>
                  Release {po}
                </button>
              ))}
            </div>
          )}

          {actionMsg && (
            <Alert variant={actionMsg.variant} style={{ marginTop: 12, marginBottom: 0, fontSize: 13 }}>{actionMsg.text}</Alert>
          )}
        </div>

        {/* ── Convert result — always the response, never the request ────── */}
        {convertResult && convertResult.success && (
          <div className="qw-card-flat qw-pad" style={{ border: "2px solid #A7F3D0", background: "#F0FDF4", marginBottom: 14 }}>
            <div style={{ fontWeight: 700, color: "#065F46", marginBottom: 8 }}>
              Order #{convertResult.orderNum} created
            </div>
            <div style={{ fontSize: 13, color: "#0F172A", lineHeight: 1.7 }}>
              <div>Converted shipments (as Epicor ordered them): <span className="qw-mono" style={{ fontWeight: 700 }}>{(convertResult.convertedLines || []).join(", ") || "none"}</span></div>
              {(convertResult.notConvertedLines || []).length > 0 && (
                <div style={{ color: "#991B1B", fontWeight: 600 }}>
                  Epicor declined lines {(convertResult.notConvertedLines || []).join(", ")} — they are still open and
                  the customer still expects them.
                </div>
              )}
              <div>Still open on this quote: <span className="qw-mono">{(convertResult.remainingOpenLines || []).join(", ") || "none — the plan is complete"}</span></div>
              {convertResult.shipDate && <div>Ship date: <span className="qw-mono">{String(convertResult.shipDate).slice(0, 10)}</span> (order date + 10 days)</div>}
              <div style={{ marginTop: 6 }}>
                <button className="qw-btn qw-btn-primary qw-btn-sm" onClick={() => navigate(`/order/${convertResult.orderNum}`)}>
                  View order #{convertResult.orderNum}
                </button>
              </div>
            </div>
            {(convertResult.warnings || []).length > 0 && (
              <div className="qw-info qw-info-amber" style={{ marginTop: 10, fontSize: 12.5 }}>
                <strong>Warnings</strong>
                <ul style={{ margin: "4px 0 0 18px" }}>
                  {convertResult.warnings.map((w, i) => <li key={i}>{w}</li>)}
                </ul>
              </div>
            )}
            <div style={{ fontSize: 12, color: "#64748B", marginTop: 8 }}>
              The acknowledgement email has been queued to the customer with the rep copied.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
